import { DeviceItem, LocationNode, NodeId, ScopeStats, SelectionState } from './types';

const buildNodeIndex = (nodes: LocationNode[]): Map<NodeId, LocationNode> => {
  const map = new Map<NodeId, LocationNode>();
  nodes.forEach((n) => map.set(n.id, n));
  return map;
};

export const descendantLeafDeviceIds = (
  scopeNodeId: NodeId | null,
  nodes: LocationNode[],
  devices: DeviceItem[]
): NodeId[] => {
  if (scopeNodeId === null) return devices.map((d) => d.id);

  const index = buildNodeIndex(nodes);
  const leafLabIds = new Set<NodeId>();
  const seen = new Set<NodeId>();
  const visit = (id: NodeId) => {
    if (seen.has(id)) return;
    seen.add(id);
    const node = index.get(id);
    if (!node) return;
    if (node.type === 'lab') {
      leafLabIds.add(node.id);
      return;
    }
    node.childIds.forEach(visit);
  };
  visit(scopeNodeId);

  return devices.filter((d) => leafLabIds.has(d.parentId)).map((d) => d.id);
};

export const computeStats = (scopeDeviceIds: NodeId[], selected: Set<NodeId>): ScopeStats => {
  const total = scopeDeviceIds.length;
  const selectedCount = scopeDeviceIds.reduce((acc, id) => (selected.has(id) ? acc + 1 : acc), 0);
  let state: SelectionState = 'none';
  if (total > 0 && selectedCount === total) state = 'all';
  else if (selectedCount > 0) state = 'partial';
  return { selected: selectedCount, total, state };
};

export const filterDevicesBySearch = (devices: DeviceItem[], query: string): DeviceItem[] => {
  const q = query.trim().toLowerCase();
  if (!q) return devices;
  return devices.filter((d) => d.name.toLowerCase().includes(q));
};

/**
 * Auto-fill helpers: derive parent IDs whose ALL descendant devices are selected.
 * Used to normalize device selections into Group/Lab/Device assignments.
 */
export const labsFullySelected = (
  selected: Set<NodeId>,
  nodes: LocationNode[],
  devices: DeviceItem[]
): NodeId[] => {
  const labs = nodes.filter((n) => n.type === 'lab');
  return labs
    .filter((lab) => {
      const labDeviceIds = devices.filter((d) => d.parentId === lab.id).map((d) => d.id);
      return labDeviceIds.length > 0 && labDeviceIds.every((id) => selected.has(id));
    })
    .map((l) => l.entityId);
};

// `fullyLabIds` holds lab entityIds; compare against each group's child lab entityIds.
export const groupsFullySelected = (fullyLabIds: Set<NodeId>, nodes: LocationNode[]): NodeId[] => {
  const nodeById = buildNodeIndex(nodes);
  const groups = nodes.filter((n) => n.type === 'group');
  return groups
    .filter((g) => {
      if (g.childIds.length === 0) return false;
      return g.childIds.every((childKey) => {
        const child = nodeById.get(childKey);
        return child != null && fullyLabIds.has(child.entityId);
      });
    })
    .map((g) => g.entityId);
};
